import {
  Button,
  Icon,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from 'semantic-ui-react';
import React from 'react';
import { HeaderCell } from 'common/components/Table';
import { Tooltip } from 'common/components/Tooltip';
import { TaskExecutionMetrics } from '../api/taskManagementApi';

// Helper function to get status tooltip text
const getStatusTooltip = (status: string): string => {
  switch (status) {
    case 'SUCCESS':
      return 'Task completed successfully';
    case 'FAILURE':
      return 'Task failed with an error';
    case 'PENDING':
      return 'Task is waiting to be processed';
    case 'REVOKED':
      return 'Task was cancelled or terminated';
    default:
      return 'Unknown task status';
  }
};

interface TaskListProps {
  loading: boolean;
  updating: boolean;
  setMessage: (message: { type: "error" | "info" | "success"; content: string }) => void;
  handleRunTask: (taskId: number) => void;
  handleRunAnyTask: (taskPath: string, description: string) => void;
  handleToggleEnabled: (taskId: number) => void;
  handleEdit: (taskId: number) => void;
  handleViewHistory: (taskId: number) => void;
  handleDeleteOrphanedTasks: () => void;
  handleDeleteTask: (taskId: number, taskName: string) => void;
  getFilteredTasks: () => any[];
  getAllTasksUnified: () => any[];
  getRegistryScheduleDisplay: (schedule: string) => string;
  taskMetrics?: Record<string, TaskExecutionMetrics>;
}

const TaskList = (props: TaskListProps) => {
  const { loading, updating, setMessage, handleRunTask, handleRunAnyTask, handleToggleEnabled, handleEdit, handleViewHistory, handleDeleteOrphanedTasks, handleDeleteTask, getFilteredTasks, getAllTasksUnified, getRegistryScheduleDisplay, taskMetrics } = props;
  return (
    <Table celled selectable loading={loading} compact>
      <TableHeader>
        <TableRow>
          <HeaderCell>Name</HeaderCell>
          <HeaderCell>Description</HeaderCell>
          <HeaderCell>Category</HeaderCell>
          <HeaderCell>Schedule</HeaderCell>
          <HeaderCell>Next Run</HeaderCell>
          <HeaderCell>Enabled</HeaderCell>
          <HeaderCell>Last Status</HeaderCell>
          <HeaderCell>Success Rate</HeaderCell>
          <HeaderCell>Actions</HeaderCell>
        </TableRow>
      </TableHeader>
      <TableBody>
        {getFilteredTasks().length === 0 ? (
          <TableRow>
            <TableCell colSpan={9} textAlign="center" style={{ padding: '40px' }}>
              {loading ? (
                <div>Loading tasks...</div>
              ) : getAllTasksUnified().length === 0 ? (
                <div>
                  <Icon name="warning" size="large" color="grey" />
                  <div style={{ marginTop: '8px' }}>No tasks found. Try refreshing the page.</div>
                </div>
              ) : (
                <div>
                  <Icon name="search" size="large" color="grey" />
                  <div style={{ marginTop: '8px' }}>No tasks match your search criteria.</div>
                </div>
              )}
            </TableCell>
          </TableRow>
        ) : (
          getFilteredTasks().map(task => (
            <TableRow key={task.id}>
              <TableCell $firstcol>{task.name}</TableCell>
              <TableCell style={{ maxWidth: '300px', wordBreak: 'break-word' }}>
                {task.description}
              </TableCell>
              <TableCell>
                <Label color="blue" size="small">
                  {task.category}
                </Label>
              </TableCell>
              <TableCell style={{ maxWidth: '200px', wordBreak: 'break-word' }}>
                {task.is_periodic
                  ? task.schedule
                  : task.default_schedule
                    ? getRegistryScheduleDisplay(task.default_schedule)
                    : 'No schedule'}
              </TableCell>
              <TableCell>{task.is_periodic ? task.next_run : 'N/A'}</TableCell>
              <TableCell>
                {task.is_periodic ? (
                  <Label color={task.enabled ? 'green' : 'red'} size="small">
                    {task.enabled ? 'Yes' : 'No'}
                  </Label>
                ) : (
                  <Label color="grey" size="small">
                    N/A
                  </Label>
                )}
              </TableCell>
              <TableCell>
                {(() => {
                  const metrics = taskMetrics?.[task.task_path];
                  if (!metrics || !metrics.last_execution_status) {
                    return <Label color="grey" size="small">No Data</Label>;
                  }
                  
                  const status = metrics.last_execution_status;
                  const color = status === 'SUCCESS' ? 'green' : status === 'FAILURE' ? 'red' : 'yellow';
                  const icon = status === 'SUCCESS' ? 'check' : status === 'FAILURE' ? 'times' : 'clock';
                  
                  return (
                    <Tooltip content={getStatusTooltip(status)}>
                      <Label color={color} size="small">
                        <Icon name={icon} />
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </Label>
                    </Tooltip>
                  );
                })()}
              </TableCell>
              <TableCell>
                {(() => {
                  const metrics = taskMetrics?.[task.task_path];
                  if (!metrics || metrics.success_rate === null) {
                    return <Label color="grey" size="small">No Data</Label>;
                  }
                  
                  const rate = metrics.success_rate;
                  const color = rate >= 90 ? 'green' : rate >= 70 ? 'yellow' : 'red';
                  
                  return (
                    <Label color={color} size="small">
                      {rate}%
                    </Label>
                  );
                })()}
              </TableCell>
              <TableCell>
                <div style={{ display: 'flex', gap: '4px' }}>
                  {(task.status !== 'orphaned') && (
                    <>
                      <Button
                        icon={<Icon name="play circle" />}
                        size="mini"
                        color="green"
                        onClick={e => {
                          e.stopPropagation();
                          if (task.is_periodic) {
                            handleRunTask(task.periodic_task);
                          } else {
                            handleRunAnyTask(task.task_path, task.description);
                          }
                        }}
                        disabled={updating}
                        title="Run Now"
                      />
                      {task.is_periodic &&
                        <Button
                          icon={<Icon name="toggle on" />}
                          size="mini"
                          color={task.enabled ? "yellow" : "green"}
                          onClick={e => {
                            e.stopPropagation();
                            handleToggleEnabled(task.periodic_task);
                          }}
                          disabled={updating}
                          title={task.enabled ? "Disable Task" : "Enable Task"}
                        />}
                      <Button
                        icon={<Icon name="edit" />}
                        size="mini"
                        onClick={e => {
                          e.stopPropagation();
                          if (task.is_periodic) {
                            handleEdit(task.periodic_task);
                          } else {
                            handleEdit(task.registry_task);
                          }
                        }}
                        title="Edit"
                      />
                      {task.is_periodic && (
                        <Button
                          icon={<Icon name="history" />}
                          size="mini"
                          onClick={e => {
                            e.stopPropagation();
                            handleViewHistory(task.periodic_task.id);
                          }}
                          title="View Execution History"
                        />
                      )}
                    </>
                  )}
                  {task.status === 'orphaned' && (
                    <Button
                      icon={<Icon name="trash" />}
                      size="mini"
                      color="red"
                      onClick={e => {
                        e.stopPropagation();
                        handleDeleteTask(task.orphaned_task.id, task.name);
                      }}
                      disabled={updating}
                      title="Delete This Orphaned Task"
                    />
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
};

export default TaskList;
